import React, { useState } from "react";
import { Pressable, Text, StyleSheet, View } from "react-native";
import {
  colors,
  spacing,
  radii,
  typographyStyles,
  typographyScale,
  webTransitions,
} from "../../../styles/design-system";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import AxioCard from "../../axio-ds/Card/AxioCard";

export type StatCardColorType = "blue" | "purple" | "green" | "orange" | "red";

const colorConfig: Record<
  StatCardColorType,
  {
    border: string;
    hoverBorder: string;
    hoverShadow: string;
    iconBg: string;
    iconHoverBg: string;
    iconColor: string;
    accentText: string;
  }
> = {
  blue: {
    border: colors.blue[100],
    hoverBorder: colors.blue[200],
    hoverShadow: colors.blue[100],
    iconBg: colors.blue[50],
    iconHoverBg: colors.blue[100],
    iconColor: colors.blue[500],
    accentText: colors.blue[700],
  },
  purple: {
    border: colors.purple[100],
    hoverBorder: colors.purple[200],
    hoverShadow: colors.purple[100],
    iconBg: colors.purple[50],
    iconHoverBg: colors.purple[100],
    iconColor: colors.purple[500],
    accentText: colors.purple[700],
  },
  green: {
    border: colors.success[100],
    hoverBorder: colors.success[200],
    hoverShadow: colors.success[100],
    iconBg: colors.success[50],
    iconHoverBg: colors.success[100],
    iconColor: colors.success[500],
    accentText: colors.success[700],
  },
  orange: {
    border: colors.warning[100],
    hoverBorder: colors.warning[200],
    hoverShadow: colors.warning[100],
    iconBg: colors.warning[50],
    iconHoverBg: colors.warning[100],
    iconColor: colors.warning[400],
    accentText: colors.warning[700],
  },
  red: {
    border: colors.error[100],
    hoverBorder: colors.error[200],
    hoverShadow: colors.error[100],
    iconBg: colors.error[50],
    iconHoverBg: colors.error[100],
    iconColor: colors.error[500],
    accentText: colors.error[700],
  },
};

interface StatCardProps {
  title: string;
  value: string;
  change: string;
  changeType: "positive" | "negative";
  icon: PhosphorIcon;
  color: StatCardColorType;
  footer?: React.ReactNode;
}

export function StatCard({ title, value, change, changeType, icon: Icon, color, footer }: StatCardProps) {
  const colorStyles = colorConfig[color];
  const [isHovered, setIsHovered] = useState(false);

  const shadowStyle = isHovered
    ? {
        shadowColor: colorStyles.hoverShadow,
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.5,
        shadowRadius: 25,
        elevation: 8,
      }
    : {
        shadowColor: colors.grey[900],
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 6,
        elevation: 4,
      };

  return (
    <Pressable
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={styles.pressableWrapper}>
      <AxioCard
        variant="basic"
        style={[
          styles.container,
          shadowStyle,
          { borderColor: isHovered ? colorStyles.hoverBorder : colorStyles.border },
        ]}>
        <View style={styles.content}>
          <View style={styles.header}>
            <View
              style={[
                styles.iconContainer,
                { backgroundColor: isHovered ? colorStyles.iconHoverBg : colorStyles.iconBg },
              ]}>
              <Icon size={24} color={colorStyles.iconColor} weight="regular" />
            </View>
            {change && (
              <View
                style={[
                  styles.changeBadge,
                  changeType === "positive" ? styles.changeBadgePositive : styles.changeBadgeNegative,
                ]}>
                <Text
                  style={[
                    styles.changeText,
                    changeType === "positive" ? styles.changeTextPositive : styles.changeTextNegative,
                  ]}>
                  {change}
                </Text>
              </View>
            )}
          </View>
          <Text style={styles.valueText}>{value}</Text>
          <Text style={styles.titleText}>{title}</Text>
          {footer && <View style={styles.footerContainer}>{footer}</View>}
        </View>
      </AxioCard>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressableWrapper: {
    width: 240,
    height: 220,
    borderRadius: 24,
    // @ts-expect-error web-only property
    cursor: "default",
  },
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderRadius: 24,
    padding: spacing.xl,
    borderWidth: 1,
    ...webTransitions.fast,
  },
  content: {
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: spacing.lg,
  },
  footerContainer: {
    marginTop: 6,
  },
  iconContainer: {
    width: spacing["3xl"],
    height: spacing["3xl"],
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  changeBadge: {
    paddingHorizontal: 10,
    paddingVertical: spacing.xxs,
    borderRadius: radii.full,
  },
  changeBadgePositive: {
    backgroundColor: colors.success[50],
  },
  changeBadgeNegative: {
    backgroundColor: colors.error[50],
  },
  changeText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
  },
  changeTextPositive: {
    color: colors.success[700],
  },
  changeTextNegative: {
    color: colors.error[700],
  },
  valueText: {
    fontSize: typographyScale.displayXl.fontSize,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[900],
    marginBottom: spacing.xs,
  },
  titleText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 1,
  },
});
